// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import xScreen from "@bg/XDL/Utilities/xScreen";
import WidgetTitle from "@bgs/App/Components/WidgetTitle";
import { bgsCarouselStyles, BGSProductCarouselSlider } from "@bgs/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    brandsSelected = "brandsSelected",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    displayBrandNames = "displayBrandNames",
    viewAllLinkTitle = "viewAllLinkTitle",
    viewAllLink = "viewAllLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumBrandImageHeight]: number;
        [fields.displayBrandNames]: boolean;
        [fields.viewAllLinkTitle]: string;
        [fields.viewAllLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText,name",
    };
    const brandsPageLink = getPageLinkByPageType(state, "BrandsPage");
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;
    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        brandsPageLink,
        isMobileScreen: state.commonActions["isMobileScreen"],
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    ThemeProps<BaseTheme> &
    HasShellContext;

interface State {
    paginationText: string;
}
export interface BrandListStyles extends BGSProductCarouselSlider {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    brandName?: TypographyProps;
    gridWrapper?: InjectableCss;
    slideWrapper?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    ...bgsCarouselStyles,
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-align: center;
        `,
    },
    link: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            &:hover {
                > div {
                    filter: grayscale(0%);
                }
            }
            ${xScreen("gt", "lg")} {
                width: calc(100% / 7);
            }
            ${xScreen("lte", "md")} {
                width: calc(25% - 20px);
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
                min-height: 100px;
            }
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
    image: {
        css: css`
            width: 133px;
            height: 100%;
            filter: grayscale(100%);
        `,
    },
    brandName: {
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        ellipsis: true,
        css: css`
            margin-top: 9px;
            padding: 0px 12px 20px 12px;
            text-align: center;
            opacity: 0.6;
        `,
    },
    slide: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
        `,
    },
    gridWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 30px;
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    mainContainer: {
        css: css`
            width: 100%;
            position: relative;
            @media print {
                display: none;
            }
            ${xScreen("gt", "lg")} {
                display: none;
            }
        `,
    },
    slideWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            width: 100%;
            min-width: 100%;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                justify-content: center;
            }
        `,
    },
};

const styles = listStyles;

const SponsoredLogos: React.FC<Props> = ({
    loadBrandGallery,
    shouldLoadBrandGallery,
    brandGalleryParameter,
    brandsState,
    fields,
    brandsPageLink,
    shellContext,
    theme,
    isMobileScreen,
}) => {
    const { isLoading, value: brandList } = brandsState;
    useEffect(() => {
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }, []);
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [currentGroupIndex, setCurrentGroupIndex] = React.useState(0);
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        const newSelectedIndex = embla ? embla.selectedScrollSnap() : 0;
        setSelectedIndex(newSelectedIndex);
        setCurrentGroupIndex(Math.floor(newSelectedIndex));
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        return 1;
    }, [windowResizeTime]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[3];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [brandList, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        const newSelectedIndex = embla ? embla.selectedScrollSnap() : 0;
        setSelectedIndex(newSelectedIndex);
        setCurrentGroupIndex(Math.floor(newSelectedIndex / slidesPerView));

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !brandList || brandList.length === 0) {
            return;
        }
        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: false,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, brandList]);

    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${fields.maximumBrandImageHeight}px`,
        },
    };

    const slidesPerView = isMobileScreen ? 3 : 12;

    if (isLoading || !brandList || !brandList.length) {
        return null;
    }

    const totalGroups = Math.ceil(brandList.length / slidesPerView);

    return (
        <>
            <WidgetTitle title={fields.title} viewAllLink={brandsPageLink!.url} />
            <StyledWrapper {...styles.gridWrapper}>
                {brandList.map(brand => (
                    <Clickable href={brand.detailPagePath} {...styles.link} key={`popular_category_${brand.id}`}>
                        <LazyImage
                            imgProps={imgProps}
                            {...styles.image}
                            src={brand.logoSmallImagePath}
                            altText={
                                brand.logoAltText
                                    ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                    : `${translate("Go to brand page")} ${brand.name}`
                            }
                        />
                        {fields.displayBrandNames && <Typography {...styles.brandName}>{brand.name}</Typography>}
                    </Clickable>
                ))}
            </StyledWrapper>
            <StyledWrapper {...styles.mainContainer}>
                {brandList.length > slidesPerView && (
                    <StyledWrapper {...styles.navigationWrapper}>
                        <Button
                            {...styles.scrollPrev}
                            disabled={!canScrollPrev}
                            onClick={() => embla && embla.scrollPrev()}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                        <Typography weight={fontConfig.weight.semiBold} lineHeight="24px" italic={true}>{`${
                            currentGroupIndex + 1
                        } of ${totalGroups}`}</Typography>
                        <Button
                            {...styles.scrollPrev}
                            disabled={!canScrollNext}
                            onClick={() => embla && embla.scrollNext()}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.carouselWrapper} ref={emblaRef}>
                    <StyledWrapper data-test-selector="slides" {...styles.innerContainer}>
                        {brandList.map((brand, index) => {
                            if (index % slidesPerView === 0) {
                                return (
                                    <StyledWrapper key={`grouped_slide_${index}`} {...styles.slideWrapper}>
                                        {brandList.slice(index, index + slidesPerView).map((brandInRow, idx) => (
                                            <Clickable
                                                href={brandInRow.detailPagePath}
                                                {...styles.link}
                                                key={`mobile_sponsored_logo_${brandInRow.id}`}
                                            >
                                                <LazyImage
                                                    imgProps={imgProps}
                                                    {...styles.image}
                                                    src={brandInRow.logoSmallImagePath}
                                                    altText={
                                                        brandInRow.logoAltText
                                                            ? `${translate("Go to brand page")} ${
                                                                  brandInRow.logoAltText
                                                              }`
                                                            : `${translate("Go to brand page")} ${brandInRow.name}`
                                                    }
                                                />
                                                {fields.displayBrandNames && (
                                                    <Typography {...styles.brandName}>{brandInRow.name}</Typography>
                                                )}
                                            </Clickable>
                                        ))}
                                    </StyledWrapper>
                                );
                            }
                            return null;
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(SponsoredLogos))),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Sponsored Logos",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Our Brands",
                fieldType: "Contextual",
            },
            {
                name: fields.viewAllLinkTitle,
                displayName: "View All Link Title",
                editorTemplate: "TextField",
                defaultValue: "View All",
                fieldType: "Contextual",
            },
            {
                name: fields.viewAllLink,
                displayName: "View All Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Url",
                },
                fieldType: "Contextual",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                defaultValue: [],
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
            {
                name: fields.displayBrandNames,
                displayName: "Display Brand Names",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
